import { Injectable } from "@angular/core";
import { LocaleService } from "../i18n/locale.service";

type DepartmentTranslationMap = Record<
  string,
  {
    label?: string;
    icon?: string;
  }
>;

@Injectable({
  providedIn: "root",
})
export class DepartmentLocalizationService {
  private departmentMap = new Map<string, string>();
  private reverseMap = new Map<string, string>();
  private iconMap = new Map<string, string>();

  private loaded = false;
  private loadedLocale: string | null = null;

  constructor(private localeService: LocaleService) {}

  async getLabel(value: string): Promise<string> {
    await this.ensureMapLoaded();
    return this.departmentMap.get(value) ?? value;
  }

  async getValue(label: string): Promise<string> {
    await this.ensureMapLoaded();
    return this.reverseMap.get(this.normalizeStr(label)) ?? label;
  }

  async getIcon(value: string): Promise<string | undefined> {
    await this.ensureMapLoaded();
    return this.iconMap.get(value);
  }

  async reload(): Promise<void> {
    this.loaded = false;
    this.departmentMap.clear();
    this.reverseMap.clear();
    this.iconMap.clear();
    await this.ensureMapLoaded();
  }

  async getAll(): Promise<{ label: string; value: string; icon?: string }[]> {
    await this.ensureMapLoaded();

    const list: { label: string; value: string; icon?: string }[] = [];

    this.departmentMap.forEach((label, value) => {
      list.push({
        label,
        value,
        icon: this.iconMap.get(value),
      });
    });

    return list;
  }

  private async ensureMapLoaded(): Promise<void> {
    const locale = this.localeService.getCurrentLocale();
    if (
      this.loaded &&
      this.loadedLocale === locale &&
      this.departmentMap.size > 0
    ) {
      return;
    }

    this.departmentMap.clear();
    this.reverseMap.clear();
    this.iconMap.clear();

    const departmentMap =
      this.localeService.getValue<DepartmentTranslationMap>("departments") ?? {};

    Object.entries(departmentMap).forEach(([value, department]) => {
      const label = department?.label ?? value;
      this.departmentMap.set(value, label);
      this.reverseMap.set(this.normalizeStr(label), value);

      if (department?.icon) {
        this.iconMap.set(value, department.icon);
      }
    });

    this.loaded = true;
    this.loadedLocale = locale;
  }

  private normalizeStr(s?: string): string {
    if (!s) return "";

    return s
      .normalize("NFKC")
      .replace(/&/g, " ")
      .replace(/\band\b/gi, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }
}
